<template>
  <div class="sg-calendar">
    <div class="sg-calendar__header">
      <div class="sg-calendar__title">{{ i18nDate }}</div>
      <div v-if="validatedRange.length === 0" class="sg-calendar__button-group">
        
              <sg-button
                @click="selectDate('prev-month')"
              >
              {{ t('sg.datepicker.prevMonth') }}
            </sg-button>
            <sg-button  @click="selectDate('today')">
              {{
                t('sg.datepicker.today')
              }}
            </sg-button>
            <sg-button
              @click="selectDate('next-month')"
            >
              {{ t('sg.datepicker.nextMonth') }}
            </sg-button>
     
      </div>
    </div>
    <div v-if="validatedRange.length === 0" class="sg-calendar__body">
      <date-table
        :date="date"
        :selected-day="realSelectedDay"
        @pick="pickDay"
      >
        <template v-if="$slots.dateCell" #dateCell="data">
          <slot name="dateCell" v-bind="data"></slot>
        </template>
      </date-table>
    </div>
    <div v-else class="sg-calendar__body">
      <date-table
        v-for="(range_, index) in validatedRange"
        :key="index"
        :date="range_[0]"
        :selected-day="realSelectedDay"
        :range="range_"
        :hide-header="index !== 0"
        @pick="pickDay"
      >
        <template v-if="$slots.dateCell" #dateCell="data">
          <slot name="dateCell" v-bind="data"></slot>
        </template>
      </date-table>
    </div>
  </div>
</template>

<script lang="ts">
import SgButton from '../../button/index'
import SgButtonGroup from '../../button/index'
import { useLocale } from '@sgui-plus/hooks'
import DateTable from './date-table.vue'
import {
  ref,
  ComputedRef,
  PropType,
  computed,
  defineComponent,
} from 'vue'
import dayjs, { Dayjs } from 'dayjs'

type DateType = 'prev-month' | 'today' | 'next-month'

export default defineComponent({
  name: 'SgCalendar',

  components: {
    DateTable,
    SgButton,
    SgButtonGroup
  },

  props: {
    modelValue: {
      type: Date,
    },
    range: {
      type: Array as PropType<Array<Date>>,
      validator: (range: Date): boolean => {
        if (Array.isArray(range)) {
          return (
            range.length === 2 &&
            range.every(
              item => item instanceof Date,
            )
          )
        }
        return false
      },
    },
  },

  emits: ['input', 'update:modelValue'],
  setup(props, ctx) {
    const { t, lang } = useLocale()
    const selectedDay = ref<any>(null)
    const now = dayjs().locale(lang.value)

    const prevMonthDayjs = computed(() => {
      return date.value.subtract(1, 'month')
    })
    const curMonthDatePrefix = computed(() => {
      return dayjs(date.value).locale(lang.value).format('YYYY-MM')
    })

    const nextMonthDayjs = computed(() => {
      return date.value.add(1, 'month')
    })

    const i18nDate = computed(() => {
      const pickedMonth = `sg.datepicker.month${date.value.format('M')}`
      return `${date.value.year()} ${t('sg.datepicker.year')} ${t(pickedMonth)}`
    })

    const realSelectedDay = computed<any>({
      get() {
        if (!props.modelValue) return selectedDay.value
        return date.value
      },
      set(val: Dayjs) {
        selectedDay.value = val
        const result = val.toDate()
        ctx.emit('input', result)
        ctx.emit('update:modelValue', result)
      },
    })

    const date: ComputedRef<Dayjs> = computed(() => {
      if (!props.modelValue) {
        if (realSelectedDay.value) {
          return realSelectedDay.value
        } else if (validatedRange.value.length) {
          return validatedRange.value[0][0]
        }
        return now
      } else {
        return dayjs(props.modelValue).locale(lang.value)
      }
    })

    // if range is valid, we get a two-digit array
    const validatedRange = computed(() => {
      if (!props.range) return []
      const rangeArrDayjs = props.range.map(_ => dayjs(_).locale(lang.value))
      const [startDayjs, endDayjs] = rangeArrDayjs
      if (startDayjs.isAfter(endDayjs)) {
        console.warn(
          '[SgCalendar]end time should be greater than start time',
        )
        return []
      }
      if (startDayjs.isSame(endDayjs, 'month')) {
        // same month
        return [[
          startDayjs.startOf('week'),
          endDayjs.endOf('week'),
        ]]
      } else {
        // two months
        if (startDayjs.add(1, 'month').month() !== endDayjs.month()) {
          console.warn(
            '[SgCalendar]start time and end time interval must not exceed two months',
          )
          return []
        }
        const endMonthFirstDay = endDayjs.startOf('month')
        const endMonthFirstWeekDay = endMonthFirstDay.startOf('week')
        let endMonthStart = endMonthFirstDay
        if (!endMonthFirstDay.isSame(endMonthFirstWeekDay, 'month')) {
          endMonthStart = endMonthFirstDay.endOf('week').add(1, 'day')
        }
        return [
          [
            startDayjs.startOf('week'),
            startDayjs.endOf('month'),
          ],
          [
            endMonthStart,
            endDayjs.endOf('week'),
          ],
        ]
      }
    })

    const pickDay = (day: Dayjs) => {
      realSelectedDay.value = day
    }

    const selectDate = (type: DateType) => {
      let day: Dayjs
      if (type === 'prev-month') {
        day = prevMonthDayjs.value
      } else if (type === 'next-month') {
        day = nextMonthDayjs.value
      } else {
        day = now
      }

      if (day.isSame(date.value, 'day')) return
      pickDay(day)
    }

    return {
      selectedDay,
      curMonthDatePrefix,
      i18nDate,
      realSelectedDay,
      date,
      validatedRange,
      pickDay,
      selectDate,
      t,
    }
  },
})
</script>
